import { useState, useEffect, lazy, Suspense } from 'react'
import { Routes, Route, Navigate, useNavigate } from 'react-router-dom'
import { useAuth } from './contexts/AuthContext'
import { supabase } from './lib/supabase'
import Spinner from './components/ui/Spinner'

// Layouts — loaded eagerly (shell UI)
import DashboardLayout from './components/layout/DashboardLayout'
import AdminLayout from './components/layout/AdminLayout'
import CollabLayout from './components/layout/CollabLayout'
import PublicLayout from './components/layout/PublicLayout'

// --- Lazy-loaded pages ---

// Public
const LandingPage = lazy(() => import('./pages/LandingPage'))
const LoginPage = lazy(() => import('./pages/LoginPage'))
const SpinPage = lazy(() => import('./pages/SpinPage'))
const RedeemPage = lazy(() => import('./pages/RedeemPage'))
const OfferPage = lazy(() => import('./pages/OfferPage'))
const NotFoundPage = lazy(() => import('./pages/NotFoundPage'))
const OnboardingPage = lazy(() => import('./pages/OnboardingPage'))

// Legal
const MentionsLegalesPage = lazy(() => import('./pages/legal/MentionsLegalesPage'))
const CGUPage = lazy(() => import('./pages/legal/CGUPage'))
const CGVPage = lazy(() => import('./pages/legal/CGVPage'))
const ConfidentialitePage = lazy(() => import('./pages/legal/ConfidentialitePage'))

// Dashboard (merchant)
const DashboardPage = lazy(() => import('./pages/DashboardPage'))
const DashboardOffersPage = lazy(() => import('./pages/DashboardOffersPage'))
const DashboardStatsPage = lazy(() => import('./pages/DashboardStatsPage'))
const DashboardSettingsPage = lazy(() => import('./pages/DashboardSettingsPage'))
const DashboardFinancePage = lazy(() => import('./pages/DashboardFinancePage'))
const DashboardEmailsPage = lazy(() => import('./pages/DashboardEmailsPage'))
const DashboardTagsPage = lazy(() => import('./pages/DashboardTagsPage'))

// Admin
const AdminDashboardPage = lazy(() => import('./pages/admin/AdminDashboardPage'))
const AdminMerchantsPage = lazy(() => import('./pages/admin/AdminMerchantsPage'))
const AdminTagsPage = lazy(() => import('./pages/admin/AdminTagsPage'))
const AdminMapPage = lazy(() => import('./pages/admin/AdminMapPage'))
const AdminSuggestionsPage = lazy(() => import('./pages/admin/AdminSuggestionsPage'))
const PipelinePage = lazy(() => import('./pages/admin/PipelinePage'))
const ClientDetailPage = lazy(() => import('./pages/admin/ClientDetailPage'))
const StockPage = lazy(() => import('./pages/admin/StockPage'))
const EquipePage = lazy(() => import('./pages/admin/EquipePage'))
const FinancesPage = lazy(() => import('./pages/admin/FinancesPage'))
const RoadmapPage = lazy(() => import('./pages/admin/RoadmapPage'))
const TicketsPage = lazy(() => import('./pages/admin/TicketsPage'))
const DecisionsPage = lazy(() => import('./pages/admin/DecisionsPage'))
const JournalPage = lazy(() => import('./pages/admin/JournalPage'))
const NotificationsPage = lazy(() => import('./pages/admin/NotificationsPage'))
const ChevaletsPage = lazy(() => import('./pages/admin/ChevaletsPage'))
const ChevaletsLierPage = lazy(() => import('./pages/admin/ChevaletsLierPage'))
const ChevaletsQrPage = lazy(() => import('./pages/admin/ChevaletsQrPage'))
const PresencePage = lazy(() => import('./pages/admin/PresencePage'))
const DowndetectorPage = lazy(() => import('./pages/admin/DowndetectorPage'))

// Collab
const CollabDashboardPage = lazy(() => import('./pages/collab/CollabDashboardPage'))
const CollabClientsPage = lazy(() => import('./pages/collab/CollabClientsPage'))
const CollabClientDetailPage = lazy(() => import('./pages/collab/CollabClientDetailPage'))
const CollabStockPage = lazy(() => import('./pages/collab/CollabStockPage'))
const CollabTicketsPage = lazy(() => import('./pages/collab/CollabTicketsPage'))
const CollabJournalPage = lazy(() => import('./pages/collab/CollabJournalPage'))
const CollabNotificationsPage = lazy(() => import('./pages/collab/CollabNotificationsPage'))
const CollabChevaletsLierPage = lazy(() => import('./pages/collab/CollabChevaletsLierPage'))

// Components
const ChevaletRedirect = lazy(() => import('./components/ChevaletRedirect'))
const ActivatePage = lazy(() => import('./pages/ActivatePage'))

// Suspense fallback
function PageLoader() {
  return (
    <div className="min-h-screen flex items-center justify-center" role="status" aria-label="Chargement de la page">
      <Spinner size="lg" />
    </div>
  )
}

function ProtectedRoute({ children }) {
  const { user, loading } = useAuth()
  if (loading) return <PageLoader />
  if (!user) return <Navigate to="/login" replace />
  return children
}

function AdminRoute({ children }) {
  const { user, loading: authLoading, isAdmin, checkIsAdmin } = useAuth()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (authLoading) return
    if (!user) { setReady(true); return }
    if (isAdmin !== null) { setReady(true); return }
    checkIsAdmin().then(() => setReady(true))
  }, [user, authLoading, isAdmin, checkIsAdmin])

  if (authLoading || !ready || (user && isAdmin === null)) {
    return <PageLoader />
  }
  if (!user) return <Navigate to="/login" replace />
  if (isAdmin !== true) return <Navigate to="/dashboard" replace />
  return children
}

function CollabRoute({ children }) {
  const { user, loading: authLoading, equipeRole, checkIsAdmin } = useAuth()
  const [ready, setReady] = useState(false)

  useEffect(() => {
    if (authLoading) return
    if (!user) { setReady(true); return }
    if (equipeRole !== null) { setReady(true); return }
    checkIsAdmin().then(() => setReady(true))
  }, [user, authLoading, equipeRole, checkIsAdmin])

  if (authLoading || !ready || (user && equipeRole === null)) {
    return <PageLoader />
  }
  if (!user) return <Navigate to="/login" replace />
  if (equipeRole === 'fondateur' || equipeRole === 'admin') return <Navigate to="/admin" replace />
  if (equipeRole !== 'commercial') return <Navigate to="/dashboard" replace />
  return children
}

// Redirige vers /activate si le hash contient un token recovery/invite.
// /activate est une page dédiée — pas de détection de mode nécessaire.
function AuthHashRedirect() {
  const navigate = useNavigate()
  useEffect(() => {
    const hash = sessionStorage.getItem('__auth_hash') || window.location.hash
    if (!hash) return
    const params = new URLSearchParams(hash.slice(1))
    const type = params.get('type')
    if ((type === 'invite' || type === 'recovery') && window.location.pathname !== '/activate') {
      navigate('/activate', { replace: true })
    }
  }, [navigate])
  return null
}

// Valid shortCode pattern: 3-30 alphanumeric chars + underscores
const SHORT_CODE_RE = /^[a-zA-Z0-9_]{3,30}$/

function ShortCodeRoute() {
  const params = new URL(window.location.href)
  const path = params.pathname.slice(1) // remove leading /

  if (!SHORT_CODE_RE.test(path)) {
    return <NotFoundPage />
  }

  return <SpinPage />
}

export default function App() {
  return (
    <Suspense fallback={<PageLoader />}>
      <AuthHashRedirect />
      <Routes>
        {/* Public routes */}
        <Route element={<PublicLayout />}>
          <Route path="/" element={<LandingPage />} />
          <Route path="/signup" element={<Navigate to="/login" replace />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/redeem" element={<RedeemPage />} />
          <Route path="/redeem/:qrToken" element={<RedeemPage />} />
          <Route path="/offer/:qrToken" element={<OfferPage />} />
          <Route path="/activate" element={<ActivatePage />} />
          <Route path="/mentions-legales" element={<MentionsLegalesPage />} />
          <Route path="/cgu" element={<CGUPage />} />
          <Route path="/cgv" element={<CGVPage />} />
          <Route path="/confidentialite" element={<ConfidentialitePage />} />
        </Route>

        {/* Onboarding (protected) */}
        <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />

        {/* Dashboard (protected) */}
        <Route
          path="/dashboard"
          element={
            <ProtectedRoute>
              <DashboardLayout />
            </ProtectedRoute>
          }
        >
          <Route index element={<DashboardPage />} />
          <Route path="offers" element={<DashboardOffersPage />} />
          <Route path="tags" element={<DashboardTagsPage />} />
          <Route path="stats" element={<DashboardStatsPage />} />
          <Route path="finance" element={<DashboardFinancePage />} />
          <Route path="emails" element={<DashboardEmailsPage />} />
          <Route path="settings" element={<DashboardSettingsPage />} />
        </Route>

        {/* Collab (commercial only) */}
        <Route path="/collab" element={<CollabRoute><CollabLayout /></CollabRoute>}>
          <Route index element={<CollabDashboardPage />} />
          <Route path="clients" element={<CollabClientsPage />} />
          <Route path="clients/:id" element={<CollabClientDetailPage />} />
          <Route path="stock" element={<CollabStockPage />} />
          <Route path="chevalets/lier" element={<CollabChevaletsLierPage />} />
          <Route path="tickets" element={<CollabTicketsPage />} />
          <Route path="journal" element={<CollabJournalPage />} />
          <Route path="notifications" element={<CollabNotificationsPage />} />
        </Route>

        {/* Admin (protected + admin check) */}
        <Route
          path="/admin"
          element={
            <AdminRoute>
              <AdminLayout />
            </AdminRoute>
          }
        >
          <Route index element={<AdminDashboardPage />} />
          <Route path="pipeline" element={<PipelinePage />} />
          <Route path="clients/:id" element={<ClientDetailPage />} />
          <Route path="stock" element={<StockPage />} />
          <Route path="equipe" element={<EquipePage />} />
          <Route path="finances" element={<FinancesPage />} />
          <Route path="roadmap" element={<RoadmapPage />} />
          <Route path="tickets" element={<TicketsPage />} />
          <Route path="decisions" element={<DecisionsPage />} />
          <Route path="journal" element={<JournalPage />} />
          <Route path="notifications" element={<NotificationsPage />} />
          <Route path="merchants" element={<AdminMerchantsPage />} />
          <Route path="tags" element={<AdminTagsPage />} />
          <Route path="map" element={<AdminMapPage />} />
          <Route path="suggestions" element={<AdminSuggestionsPage />} />
          <Route path="chevalets" element={<ChevaletsPage />} />
          <Route path="chevalets/lier" element={<ChevaletsLierPage />} />
          <Route path="chevalets/qr" element={<ChevaletsQrPage />} />
          <Route path="presence" element={<PresencePage />} />
          <Route path="downdetector" element={<DowndetectorPage />} />
        </Route>

        {/* Route publique NFC : /c/:chevaletId → redirect vers page spin du marchand */}
        <Route path="/c/:chevaletId" element={<ChevaletRedirect />} />

        {/* Spin page — validates shortCode format before loading */}
        <Route path="/:shortCode" element={<ShortCodeRoute />} />

        {/* 404 fallback */}
        <Route path="*" element={<NotFoundPage />} />
      </Routes>
    </Suspense>
  )
}
